import { useContext, useEffect, useState, useMemo } from 'react'
import { SocketContext } from '../../../context/SocketContext';
import { serverApi } from '../../../api';

/* ─── mini componentes ─── */
const TicketBadge = ({ codigo, numero, color }) => (
    <div style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        minWidth: 46, height: 46, borderRadius: '50%',
        background: color || '#7c3aed', color: '#fff',
        fontWeight: 800, fontSize: 11, letterSpacing: 0.4, flexShrink: 0,
        boxShadow: `0 3px 10px ${color ? color + '50' : '#7c3aed40'}`,
    }}>
        {codigo?.toUpperCase()}-{numero}
    </div>
);

const TimeChip = ({ time, label, color }) => (
    <div style={{ textAlign: 'center', minWidth: 50 }}>
        <div style={{ fontWeight: 700, fontSize: 12, color, lineHeight: 1.2 }}>{time}</div>
        <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
    </div>
);

const AgentChip = ({ agent, bgColor = '#f5f3ff', textColor = '#7c3aed', dotColor = '#7c3aed' }) => (
    <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: bgColor, borderRadius: 20, padding: '2px 8px 2px 5px', maxWidth: 130,
    }}>
        <div style={{
            width: 18, height: 18, borderRadius: '50%', background: dotColor, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9, fontWeight: 800, flexShrink: 0,
        }}>
            {agent?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <span style={{ fontSize: 10, fontWeight: 600, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {agent?.toUpperCase() || '-'}
        </span>
    </div>
);

const SortBtn = ({ label, field, active, dir, onClick }) => (
    <button
        onClick={() => onClick(field)}
        style={{
            border: 'none',
            background: active ? '#1e293b' : '#f1f5f9',
            color: active ? '#fff' : '#64748b',
            borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            transition: 'all 0.15s', whiteSpace: 'nowrap',
        }}
    >
        {label}
        {active && <span style={{ fontSize: 9 }}>{dir === 'asc' ? '↑' : '↓'}</span>}
    </button>
);

const EmptyState = ({ message, emoji = '⏸️' }) => (
    <div style={{ textAlign: 'center', padding: '30px 20px', color: '#94a3b8' }}>
        <div style={{ fontSize: 28, marginBottom: 6 }}>{emoji}</div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{message}</div>
    </div>
);

/* ─── componente principal ─── */
export const ListTicketsPausados = ({ usuario, dataFromTable, ticketActual, setCantidadPausados }) => {
    const [pausadosLista, setPausadosLista] = useState([]);
    const [hoveredId, setHoveredId] = useState(null);
    const { socket } = useContext(SocketContext);
    const [searchInput, setSearchInput] = useState('');
    const [sortBy, setSortBy] = useState('hora');
    const [sortDir, setSortDir] = useState('asc');

    /* socket */
    useEffect(() => {
        if (!socket) return;
        const cargar = (pend) => {
            const asignados = (localStorage.getItem('tiposAtencion') || '').split(',').filter(Boolean);
            const filtered = pend.filter(i => asignados.includes(i.uidTipoAtencion));
            setPausadosLista(filtered);
            if (setCantidadPausados) setCantidadPausados(filtered.length);
        };
        socket.on('tickets-pausados-lista', cargar);
        return () => socket?.off('tickets-pausados-lista', cargar);
    }, [socket, setCantidadPausados]);

    /* fetch inicial */
    useEffect(() => {
        async function fetch() {
            try {
                const { codigoEntidad, codigoUbicacion, uidModulo, uidUsuario, agente } = usuario;
                const resp = await serverApi.get('/tickets/pausados-lista', {
                    params: { codigoEntidad, codigoUbicacion, uidModulo, uidUsuario, agente, page: 1, limit: 100 }
                });
                if (resp.data.ok) {
                    const asignados = (localStorage.getItem('tiposAtencion') || '').split(',').filter(Boolean);
                    const filtered = resp.data.pausados.filter(i => asignados.includes(i.uidTipoAtencion));
                    setPausadosLista(filtered);
                    if (setCantidadPausados) setCantidadPausados(filtered.length);
                }
            } catch (e) { console.error(e); }
        }
        fetch();
    }, [usuario, setCantidadPausados]);

    const handleSort = (field) => {
        if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortBy(field); setSortDir('asc'); }
    };

    const llamarPorTicket = async (ev, uidTicketFlujo) => {
        ev.preventDefault();
        try {
            const { codigoEntidad, codigoUbicacion, uidModulo, uidUsuario, agente } = usuario;
            const resp = await serverApi.post('/tickets/llamar-por-ticket-pausado', {
                codigoEntidad, codigoUbicacion, uidModulo, uidUsuario, agente,
                uidTicketFlujo, ticketAnterior: ticketActual,
            });
            if (resp.data.ok) dataFromTable(resp.data.ticket);
            else console.error(resp.data.error);
        } catch (e) { console.error(e); }
    };

    const sorted = useMemo(() => {
        const q = searchInput.toLowerCase().trim();
        const list = pausadosLista.filter(i =>
            !q ||
            i.nombrePaciente?.toLowerCase().includes(q) ||
            i.numeroDocumento?.toLowerCase().includes(q) ||
            `${i.codigoTipoAtencion}-${i.numeroTicket}`.toLowerCase().includes(q) ||
            i.tipoAtencion?.toLowerCase().includes(q)
        );
        return [...list].sort((a, b) => {
            let va, vb;
            if (sortBy === 'hora') { va = a.hora || ''; vb = b.hora || ''; }
            else if (sortBy === 'numero') { va = a.numeroTicket || 0; vb = b.numeroTicket || 0; }
            else { va = (a.nombrePaciente || a.numeroDocumento || '').toLowerCase(); vb = (b.nombrePaciente || b.numeroDocumento || '').toLowerCase(); }
            if (va < vb) return sortDir === 'asc' ? -1 : 1;
            if (va > vb) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [pausadosLista, searchInput, sortBy, sortDir]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

            {/* BUSCADOR */}
            <div style={{ position: 'relative', marginBottom: 10 }}>
                <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#94a3b8' }}>🔍</span>
                <input
                    type="text"
                    placeholder="Buscar paciente o ticket pausado..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    style={{
                        width: '100%', padding: '8px 12px 8px 34px', borderRadius: 10,
                        border: '1.5px solid #e2e8f0', background: '#f8fafc',
                        fontSize: 13, color: '#1e293b', outline: 'none', boxSizing: 'border-box',
                    }}
                />
            </div>

            {/* SORT + COUNTER */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginRight: 2 }}>Ordenar:</span>
                <SortBtn label="Hora" field="hora" active={sortBy === 'hora'} dir={sortDir} onClick={handleSort} />
                <SortBtn label="N° Ticket" field="numero" active={sortBy === 'numero'} dir={sortDir} onClick={handleSort} />
                <SortBtn label="Nombre" field="nombre" active={sortBy === 'nombre'} dir={sortDir} onClick={handleSort} />
                <div style={{ marginLeft: 'auto' }}>
                    {sorted.length > 0 && (
                        <span style={{ background: '#f5f3ff', color: '#7c3aed', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                            {sorted.length} pausado{sorted.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>

            {/* LISTA CON SCROLL */}
            <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 2 }}>
                {sorted.length === 0
                    ? <EmptyState message="No hay tickets pausados" emoji="⏸️" />
                    : sorted.map(item => {
                        const id = item.uidTicketFlujo || item.uidTicket;
                        const hovered = hoveredId === id;
                        return (
                            <div
                                key={id}
                                onMouseEnter={() => setHoveredId(id)}
                                onMouseLeave={() => setHoveredId(null)}
                                onClick={ev => llamarPorTicket(ev, item.uidTicketFlujo)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 12,
                                    padding: '9px 14px', borderRadius: 12,
                                    background: hovered ? '#faf5ff' : '#fff',
                                    border: `1.5px solid ${hovered ? '#ddd6fe' : '#f1f5f9'}`,
                                    transition: 'all 0.15s', cursor: 'pointer',
                                    boxShadow: hovered ? '0 3px 14px #7c3aed18' : '0 1px 3px #0000000a',
                                    flexShrink: 0,
                                }}
                            >
                                <TicketBadge codigo={item.codigoTipoAtencion} numero={item.numeroTicket} color={item.color || '#7c3aed'} />

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {item.nombrePaciente?.trim() === '' ? item.numeroDocumento : item.nombrePaciente.toUpperCase()}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>
                                        {item.tipoAtencion}
                                    </div>
                                    <AgentChip agent={item.agentePausa} />
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
                                    <TimeChip time={item.hora} label="Llegada" color="#2563eb" />
                                    <TimeChip time={item.pausadoEn} label="Pausado" color="#7c3aed" />
                                </div>
                            </div>
                        );
                    })
                }
            </div>
        </div>
    );
}
